import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CreditCard,
  FileText,
  Landmark,
  Pencil,
  Receipt,
  ShieldAlert,
  ShieldCheck,
  Upload,
  WalletCards,
} from 'lucide-react';

const BANK_STATUS = {
  none: { label: 'Nicht hinterlegt', color: '#d1d5db', bg: '#374151' },
  pending: { label: 'Wird geprüft', color: '#d1d5db', bg: '#1f2937' },
  approved: { label: 'Aktiv (freigegeben)', color: '#d8b4e2', bg: '#24142a' },
  rejected: { label: 'Abgelehnt', color: '#fca5a5', bg: '#2b1111' },
};

const euro = (value) => `${Number(value || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;

export default function Balance() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [editBank, setEditBank] = useState(false);
  const [bank, setBank] = useState({ iban: '', bic: '', bank_holder: '' });
  const [busy, setBusy] = useState(false);

  const loadMe = useCallback(() => {
    api.get('/partners/me').then((response) => {
      setMe(response.data);
      const partner = response.data.partner || {};
      setBank({
        iban: partner.iban || '',
        bic: partner.bic || '',
        bank_holder: partner.bank_holder || '',
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/partners/balance').then((response) => setData(response.data)).catch(() => {
      toast.error('Kontostand konnte nicht geladen werden');
    });
    loadMe();
  }, [loadMe]);

  const saveBank = async () => {
    setBusy(true);
    try {
      await api.patch('/partners/me/bank', bank);
      toast.success('Bankverbindung gespeichert – erneute Admin-Prüfung erforderlich');
      setEditBank(false);
      loadMe();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ungültige Bankverbindung');
    } finally {
      setBusy(false);
    }
  };

  const uploadBankProof = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('document_type', 'bank_nachweis');
      await api.post('/partners/documents', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Bank-/Inhabernachweis hochgeladen');
      loadMe();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  };

  if (!data) return <div style={{ color: '#4b5563', padding: 40, textAlign: 'center' }}>Kontostand wird geladen…</div>;

  const bankStatus = BANK_STATUS[me?.partner?.bank_status] || BANK_STATUS.none;
  const proof = (me?.documents || []).find((document) => document.document_type === 'bank_nachweis');
  const kontostand = Number(data.open_projection_cents || 0) / 100;
  const inputStyle = {
    padding: '10px 12px',
    border: '1px solid #4b5563',
    borderRadius: 8,
    fontSize: 14,
    background: '#0b1220',
    color: '#f3f4f6',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -0.7, marginBottom: 5 }}>Banking</h1>
        <p style={{ color: '#4b5563', fontSize: 13, margin: 0 }}>Freigegebene Auszahlungskonten und offener, noch keiner Periode zugeordneter Kontostand bei VOOGO.</p>
      </div>

      {data.migration_blocked && (
        <div style={{ display: 'flex', gap: 10, border: '1px solid #7f1d1d', background: '#241010', borderRadius: 12, padding: 14, marginBottom: 18 }}>
          <ShieldAlert size={18} color="#fca5a5" />
          <div><strong style={{ color: '#fecaca', fontSize: 13 }}>Historischer Eröffnungsstand noch nicht freigegeben</strong><p style={{ color: '#d1d5db', fontSize: 12, margin: '3px 0 0' }}>Bis zur belegten Buchhaltungsentscheidung bleibt „ready“ fail-closed. Historische Beträge wurden nicht geschätzt.</p></div>
        </div>
      )}

      {data.period_conflict && (
        <div style={{ display: 'flex', gap: 10, border: '1px solid #7f1d1d', background: '#241010', borderRadius: 12, padding: 14, marginBottom: 18 }}>
          <ShieldAlert size={18} color="#fca5a5" />
          <div><strong style={{ color: '#fecaca', fontSize: 13 }}>Historische Abrechnungszeiträume überschneiden sich</strong><p style={{ color: '#d1d5db', fontSize: 12, margin: '3px 0 0' }}>Eine weitere Freigabe oder Auszahlung bleibt bis zur expliziten buchhalterischen Klärung gesperrt. Bestehende Belege und Beträge werden nicht verändert.</p></div>
        </div>
      )}

      <div style={{ background: '#0a0a0a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 12, padding: 20, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Landmark size={16} color="#9C27B0" /><strong style={{ fontSize: 13 }}>Bankverbindung für Auszahlungen</strong></div>
          <span style={{ fontSize: 12, fontWeight: 700, color: bankStatus.color, background: bankStatus.bg, padding: '4px 10px', borderRadius: 20 }}>{bankStatus.label}</span>
        </div>

        {!editBank ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ color: '#d1d5db', fontSize: 13 }}>
              {me?.partner?.iban ? (
                <>
                  <div><span style={{ color: '#d1d5db' }}>IBAN:</span> <strong style={{ color: '#f3f4f6' }}>{me.partner.iban}</strong></div>
                  <div><span style={{ color: '#d1d5db' }}>Inhaber:</span> {me.partner.bank_holder || '–'}{me.partner.bic ? ` · BIC ${me.partner.bic}` : ''}</div>
                </>
              ) : 'Noch keine Bankverbindung hinterlegt.'}
              <small style={{ display: 'block', marginTop: 7, color: proof?.status === 'approved' ? '#d8b4e2' : '#d1d5db' }}>
                Nachweis: {proof ? ({ approved: 'genehmigt', pending: 'in Prüfung', rejected: 'abgelehnt' }[proof.status] || proof.status) : 'fehlt'}
              </small>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={secondaryButtonStyle}><Upload size={13} /> Nachweis hochladen<input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadBankProof} style={{ display: 'none' }} disabled={busy} /></label>
              <button type="button" onClick={() => setEditBank(true)} style={primaryButtonStyle}><Pencil size={13} /> {me?.partner?.iban ? 'Bearbeiten' : 'Hinzufügen'}</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input aria-label="IBAN" placeholder="IBAN" value={bank.iban} onChange={(event) => setBank((current) => ({ ...current, iban: event.target.value }))} style={{ ...inputStyle, gridColumn: '1 / -1' }} />
            <input aria-label="BIC" placeholder="BIC, soweit erforderlich" value={bank.bic} onChange={(event) => setBank((current) => ({ ...current, bic: event.target.value }))} style={inputStyle} />
            <input aria-label="Kontoinhaber" placeholder="Kontoinhaber" value={bank.bank_holder} onChange={(event) => setBank((current) => ({ ...current, bank_holder: event.target.value }))} style={inputStyle} />
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}><button type="button" onClick={saveBank} disabled={busy} style={primaryButtonStyle}>Speichern</button><button type="button" onClick={() => setEditBank(false)} style={secondaryButtonStyle}>Abbrechen</button></div>
            <small style={{ gridColumn: '1 / -1', color: '#d1d5db' }}>Jede Änderung setzt den Status bis zur Admin-Freigabe zurück. Der Abschluss speichert Empfänger, IBAN und BIC unveränderlich vor „ready“.</small>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 12, marginBottom: 18 }}>
        {/* Der Admin erklärt für DIESELBE Zahl das Vorzeichen (admin-app/src/pages/Payouts.js:272);
            dem Partner stand hier nur ein Haussatz. Wortlaut wie auf der Einnahmen-Seite. */}
        <Metric icon={<WalletCards size={16} />} label="Kontostand bei VOOGO" value={`${kontostand >= 0 ? '+' : ''}${euro(kontostand)}`}
          detail="Plus heißt: VOOGO schuldet dir. Minus heißt: du schuldest VOOGO." />
        <Metric icon={<CreditCard size={16} />} label="Online-Bruttosumme offen" value={euro(data.pending_card_payout?.total)} detail={`${data.pending_card_payout?.count || 0} Fahrten`} />
        <Metric icon={<Receipt size={16} />} label="Bar-Bruttosumme offen" value={euro(data.cash_rides_total?.total)} detail="Bargeld wird erst beim Wochenabschluss verrechnet" />
        <Metric icon={<ShieldCheck size={16} />} label="Historische Perioden" value={String(data.payouts?.length || 0)} detail="Unveränderlich gespeichert" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: 17, background: '#0a0a0a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}><FileText size={17} color="#9C27B0" /><div><strong style={{ fontSize: 13 }}>Dokumente und Auszahlungsstatus</strong><p style={{ color: '#d1d5db', fontSize: 12, margin: '3px 0 0' }}>Umsatzübersicht, separate VOOGO-Gebührenrechnung und Detail-CSV.</p></div></div>
        <NavLink to="/settlements" style={{ ...primaryButtonStyle, textDecoration: 'none', whiteSpace: 'nowrap' }}>Zur Abrechnung</NavLink>
      </div>
    </div>
  );
}

function Metric({ icon, label, value, detail }) {
  return (
    <div style={{ background: '#0a0a0a', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#d1d5db', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>{icon}{label}</span>
      <strong style={{ display: 'block', color: '#f3f4f6', fontSize: 22, marginTop: 9 }}>{value}</strong>
      <small style={{ color: '#d1d5db' }}>{detail}</small>
    </div>
  );
}

const primaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 800,
  color: '#fff',
  background: '#8C28A0',
  border: 'none',
  borderRadius: 8,
  padding: '9px 13px',
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 700,
  color: '#f3f4f6',
  background: '#1f2937',
  border: '1px solid #4b5563',
  borderRadius: 8,
  padding: '8px 12px',
  cursor: 'pointer',
};
